// What ContextBolt SEO's and ContextBolt Bookmarks' widgets read. Each read is a small, fixed set of the board's
// free reads (shared/ours.ts): the worker runs every call through mcpCall, which refuses anything else, and nothing
// here can spend a credit or change an account. Both products answer in words written for agents, so what a widget
// shows is parsed out of them; a word that does not read is an error, never a number made up (PRODUCT.md, rule 1).
// Checked against contextbolt-seo origin/main 178efc2 and contextbolt-worker f7eae88 on 15 Oct 2026: the line
// numbers name where each answer is written. scripts/ours-stub.mjs answers in the same words.

import type { OurServer } from "./ours";
import { DAY } from "./period";
import { MONTHS } from "./calendar";

export type OurReadId = "seo-search" | "seo-wins" | "seo-ai" | "seo-keywords" | "seo-research" | "seo-credits" | "bm-library" | "bm-topics";
/** Reads a widget asks for when someone uses it (a search, a topic opened), never on a schedule. */
export type OurAskId = "bm-search" | "bm-topic";

export interface OurReadSpec {
  id: OurReadId;
  server: OurServer["id"];
  /** the tool it starts with: the source's own tool, checked against the free reads at every run */
  tool: string;
  /** seconds between reads while a board is open. Slow on purpose: SEO counts every call against 120 an hour, shared
   *  with the user's own AI (src/routes/mcp.ts:208), and Bookmarks against 200 that only reset after a quiet hour
   *  (src/routes/mcp.ts:862-869, src/lib/auth.ts:38-54) */
  every: number;
}

export const OUR_READS: Record<OurReadId, OurReadSpec> = {
  "seo-search": { id: "seo-search", server: "contextbolt-seo", tool: "list_research", every: 6 * 3600 },
  // the quick wins the user last asked SEO for, as saved: the board never runs search_console_quick_wins, which saves
  // a new entry in the user's research on every call (src/routes/mcp.ts:2071-2074)
  "seo-wins": { id: "seo-wins", server: "contextbolt-seo", tool: "list_research", every: 6 * 3600 },
  "seo-ai": { id: "seo-ai", server: "contextbolt-seo", tool: "list_research", every: 6 * 3600 },
  "seo-keywords": { id: "seo-keywords", server: "contextbolt-seo", tool: "list_research", every: 2 * 3600 },
  "seo-research": { id: "seo-research", server: "contextbolt-seo", tool: "list_research", every: 3600 },
  "seo-credits": { id: "seo-credits", server: "contextbolt-seo", tool: "check_credits", every: 3600 },
  "bm-library": { id: "bm-library", server: "contextbolt-bookmarks", tool: "export_bookmarks", every: 2 * 3600 },
  "bm-topics": { id: "bm-topics", server: "contextbolt-bookmarks", tool: "list_clusters", every: 2 * 3600 },
};

export const OUR_ASKS: Record<OurAskId, { server: OurServer["id"]; tool: string }> = {
  "bm-search": { server: "contextbolt-bookmarks", tool: "search_bookmarks" },
  "bm-topic": { server: "contextbolt-bookmarks", tool: "get_cluster_bookmarks" },
};

/** A source's read of one of ours. */
export interface OurReadConfig {
  read: OurReadId;
}

/** The read a source asked for, made safe, or an error in words. */
export function ourReadConfig(raw: unknown): OurReadConfig {
  const r = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const id = String(r.read ?? "");
  const spec = Object.hasOwn(OUR_READS, id) ? OUR_READS[id as OurReadId] : null;
  if (!spec) throw new Error("The board does not know that read.");
  return { read: spec.id };
}

export type Call = (tool: string, args: Record<string, unknown>) => Promise<unknown>;

// ---------------------------------------------------------------------------
// What each read gives a widget. Every number is one the product sent; null is "it did not say".

export interface SearchWeek {
  read: "seo-search";
  /** no snapshot saved yet */
  empty?: "none";
  site?: string;
  /** when ContextBolt SEO took it (its own saved_at) */
  at?: number | null;
  /** the seven days it covers, and the seven before them, as YYYY-MM-DD */
  from?: string | null;
  to?: string | null;
  clicks?: number | null;
  impressions?: number | null;
  /** 0 to 1 */
  ctr?: number | null;
  position?: number | null;
  dClicks?: number | null;
  dImpressions?: number | null;
  /** percentage points */
  dCtr?: number | null;
  /** positive is better: moved toward 1 */
  dPosition?: number | null;
  /** clicks a day, oldest first, for the fourteen days to `to`; fewer when Google had no row for a day */
  series?: number[];
  insights?: string[];
  queries?: { query: string; clicks: number | null; impressions: number | null; ctr: number | null; position: number | null; tag: string; dPosition: number | null }[];
}

export interface QuickWins {
  read: "seo-wins";
  /** none: no quick wins saved yet; gsc-off: Search Console not connected in SEO; gsc-pick: several properties and no
   *  default; gsc-none: none verified */
  empty?: "none" | "gsc-off" | "gsc-pick" | "gsc-none";
  site?: string;
  from?: string;
  to?: string;
  /** when ContextBolt SEO saved them */
  at?: number | null;
  /** the least impressions a query needed to count */
  min?: number | null;
  rows?: { query: string; position: number | null; impressions: number | null; clicks: number | null; ctr: number | null }[];
}

export interface AiVisibility {
  read: "seo-ai";
  empty?: "none";
  /** a full report (share of voice, rivals, engines), or a single check (mentions) */
  kind?: "report" | "check";
  brand?: string | null;
  domain?: string | null;
  topic?: string | null;
  location?: string | null;
  at?: number | null;
  /** answers naming you, over the report's twelve months */
  mentions?: number | null;
  /** answers on the topic, over the same months */
  answers?: number | null;
  /** share of voice, in percent */
  share?: number | null;
  rank?: number | null;
  rankOf?: number | null;
  leader?: string | null;
  cited?: number | null;
  pagesCited?: number | null;
  firstMonth?: string | null;
  engines?: { google: number | null; chatgpt: number | null } | null;
  sov?: { brand: string; mentions: number | null; share: number | null; you: boolean }[];
  pages?: { path: string; cites: number | null }[];
}

export interface Keywords {
  read: "seo-keywords";
  rows: { keyword: string; location: string | null; volume: number | null; difficulty: number | null; trend: number | null; cpc: number | null; intent: string | null; at: number | null; pinned: boolean }[];
}

export interface Research {
  read: "seo-research";
  rows: { tool: string; kind: string; label: string; line: string | null; at: number | null; pinned: boolean }[];
}

export interface Credits {
  read: "seo-credits";
  total: number;
  allowance: number;
  /** when this month's allowance starts again, YYYY-MM-DD */
  resets: string | null;
  topup: number;
}

export interface Saved {
  id: string;
  platform: string;
  title: string;
  author: string;
  url: string;
  /** the site, for a web page */
  host: string | null;
  tags: string[];
  topic: string | null;
  /** when it was saved; null when Bookmarks did not say */
  at: number | null;
}

export interface Library {
  read: "bm-library";
  /** every bookmark in the library */
  total: number;
  /** the newest, newest first */
  items: Saved[];
  topics: Topic[];
}

export interface Topic {
  id: string;
  label: string;
  count: number | null;
  description: string | null;
}

export interface Topics {
  read: "bm-topics";
  topics: Topic[];
}

/** A bookmark as search and a topic give it: its save date only in words ("3 days ago"), as Bookmarks writes it. */
export interface Found {
  author: string;
  source: string;
  title: string;
  url: string;
  tags: string[];
  saved: string | null;
  excerpt: string;
}

export interface FoundList {
  ask: OurAskId;
  label: string;
  items: Found[];
}

export type OurData = SearchWeek | QuickWins | AiVisibility | Keywords | Research | Credits | Library | Topics;

// ---------------------------------------------------------------------------
// What a widget works out from a read: only sums of what the product gave

/**
 * Saves a day for the last fourteen days, from the exact dates Bookmarks gave for its newest hundred. A day older
 * than the oldest of those, when the hundred do not reach back that far, was not read: it is drawn as unknown and
 * its week's count says "at least".
 */
export function savedByDay(d: Pick<Library, "items" | "total">, now = Date.now(), days = 14): { counts: (number | null)[]; week: number; before: number; weekFull: boolean; beforeFull: boolean } {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const start = today.getTime() - (days - 1) * DAY;
  const dated = d.items.filter((x) => x.at !== null);
  // every save is in hand when the library is no bigger than what was read
  const whole = d.items.length >= d.total;
  const oldest = dated.length ? Math.min(...dated.map((x) => x.at!)) : now;
  const counts: (number | null)[] = [];
  for (let i = 0; i < days; i++) {
    const from = start + i * DAY;
    const to = from + DAY;
    const known = whole || oldest < from;
    counts.push(known ? dated.filter((x) => x.at! >= from && x.at! < to).length : null);
  }
  const sum = (a: (number | null)[]) => a.reduce<number>((t, x) => t + (x ?? 0), 0);
  const week = counts.slice(-7);
  const before = counts.slice(-14, -7);
  return { counts, week: sum(week), before: sum(before), weekFull: week.every((x) => x !== null), beforeFull: before.every((x) => x !== null) };
}

// ---------------------------------------------------------------------------
// Running a read

/** An answer the board cannot read: the source keeps its last good data and says this beside it. */
export class UnreadableError extends Error {}

const unreadable = (who: string, what: string): never => {
  throw new UnreadableError(`${who} answered in a way the board cannot read (${what}). The last good data stays.`);
};

/** Run one of ours' reads: at most three free reads, each through the board's guard. */
export async function readOurs(cfg: OurReadConfig, call: Call, now = Date.now()): Promise<OurData> {
  switch (cfg.read) {
    case "seo-search":
      return searchWeek(call);
    case "seo-wins":
      return savedWins(call);
    case "seo-ai":
      return aiVisibility(call);
    case "seo-keywords":
      return keywords(await call("list_research", { tool: "keyword_difficulty", limit: 50 }));
    case "seo-research":
      return research(await call("list_research", { limit: 25 }));
    case "seo-credits":
      return credits(text(await call("check_credits", {})));
    case "bm-library": {
      const lib = library(text(await call("export_bookmarks", { limit: 100 })), now);
      // the topics' names, for the chips on each save: a library with none to name needs no second call
      const topics = lib.items.some((x) => x.topic) ? clusters(text(await call("list_clusters", {}))) : [];
      return { ...lib, topics };
    }
    case "bm-topics":
      return { read: "bm-topics", topics: clusters(text(await call("list_clusters", {}))) };
  }
}

/** Run a read a widget asks for: a search, or one topic's bookmarks. */
export async function askOurs(ask: OurAskId, args: Record<string, unknown>, call: Call): Promise<FoundList> {
  if (ask === "bm-search") {
    const query = String(args.query ?? "").trim().slice(0, 200);
    if (!query) throw new UnreadableError("Type what to look for.");
    const out = text(await call("search_bookmarks", { query, limit: 12 }));
    return { ask, label: query, items: blocks(out, /^Found \d+ (relevant )?bookmarks:/, "No bookmarks found matching that query.") };
  }
  const id = String(args.topic ?? "").trim().slice(0, 100);
  if (!id) throw new UnreadableError("Choose a topic.");
  const out = text(await call("get_cluster_bookmarks", { cluster_id: id, limit: 12 }));
  const head = /^Bookmarks in "(.*)" \(\d+\):/.exec(out);
  if (!head && /^No bookmarks found in topic: /.test(out)) return { ask, label: out.replace(/^No bookmarks found in topic: /, "").trim(), items: [] };
  return { ask, label: head?.[1] ?? id, items: blocks(out, /^Bookmarks in ".*" \(\d+\):/, null) };
}

// ---------------------------------------------------------------------------
// Reading their words

const text = (v: unknown): string => (typeof v === "string" ? v : v === null || v === undefined ? "" : JSON.stringify(v));
/** "1,742" or "n/a" (src/lib/gsc.ts:74-82): a number, or null for "it did not say" */
const num = (s: string | undefined): number | null => {
  if (s === undefined) return null;
  const n = Number(s.replace(/[,%\s]/g, ""));
  return s.trim() && Number.isFinite(n) ? n : null;
};
const finite = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown, max = 200): string | null => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : null);
/** The receipt SEO adds to an answer it saved (src/lib/research-tools.ts:49-52), which is not part of the answer. */
const unreceipt = (t: string): string => t.replace(/\n\n\[(Saved to your ContextBolt account|Research returned, but account storage failed)[^\]]*\]\s*$/, "").trim();

/** A list_research answer: {items, cursor} with each item's summary as a JSON string (src/lib/research-store.ts:76-94). */
function researchItems(v: unknown): { tool: string; label: string; at: number | null; pinned: boolean; id: string | null; args: Record<string, unknown> | null; summary: Record<string, unknown> }[] {
  const o = v && typeof v === "object" ? (v as { items?: unknown }) : null;
  if (!o || !Array.isArray(o.items)) unreadable("ContextBolt SEO", "its saved research");
  const parse = (s: unknown): Record<string, unknown> | null => {
    if (typeof s !== "string") return s && typeof s === "object" ? (s as Record<string, unknown>) : null;
    try {
      const p = JSON.parse(s);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  };
  return (o!.items as Record<string, unknown>[])
    .filter((x) => x && typeof x === "object" && typeof x.tool_name === "string")
    .map((x) => ({
      tool: String(x.tool_name),
      label: str(x.display_label, 160) ?? String(x.tool_name),
      at: finite(x.saved_at),
      pinned: !!x.bookmarked,
      id: str(x.run_id, 200),
      args: parse(x.query_args),
      summary: parse(x.summary_json) ?? {},
    }));
}

/** A saved result's answer as SEO saved it, a page of 12,000 characters at a time (src/lib/research-tools.ts:34-37): two pages at most. */
async function savedAnswer(call: Call, id: string, what: string): Promise<string> {
  let content = "";
  for (let offset: number | null = 0, pages = 0; offset !== null && pages < 2; pages++) {
    const page = (await call("get_research", { id, format: "answer", ...(offset ? { offset } : {}) })) as { content?: unknown; next_offset?: unknown } | string;
    if (!page || typeof page !== "object" || typeof page.content !== "string") unreadable("ContextBolt SEO", what);
    const p = page as { content: string; next_offset?: unknown };
    content += p.content;
    offset = typeof p.next_offset === "number" ? p.next_offset : null;
  }
  return content;
}

/** The latest nightly Search Console snapshot SEO saved (src/lib/board.ts:463-500): its answer is the snapshot as JSON. */
async function searchWeek(call: Call): Promise<SearchWeek> {
  const items = researchItems(await call("list_research", { tool: "search_console_snapshot", limit: 1 }));
  const latest = items[0];
  if (!latest?.id) return { read: "seo-search", empty: "none" };
  const content = await savedAnswer(call, latest.id, "its saved snapshot");
  let snap: Record<string, unknown>;
  try {
    snap = JSON.parse(content);
  } catch {
    return unreadable("ContextBolt SEO", "its saved snapshot");
  }
  const strip = (snap.strip && typeof snap.strip === "object" ? snap.strip : {}) as Record<string, unknown>;
  const day = typeof latest.summary.day === "string" ? latest.summary.day : null;
  // the week ends two days before the day it was taken, when Google's numbers are complete (src/lib/gsc.ts:541-544)
  const shift = (d: string | null, by: number) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(Date.parse(d + "T00:00:00Z") + by * 86_400_000).toISOString().slice(0, 10) : null);
  return {
    read: "seo-search",
    site: str(snap.site) ?? str(latest.summary.site) ?? "",
    at: latest.at,
    from: shift(day, -8),
    to: shift(day, -2),
    clicks: finite(strip.clicks),
    impressions: finite(strip.impressions),
    ctr: finite(strip.ctr),
    position: finite(strip.position),
    dClicks: finite(strip.dClicks),
    dImpressions: finite(strip.dImpressions),
    dCtr: finite(strip.dCtr),
    dPosition: finite(strip.dPosition),
    series: Array.isArray(snap.series) ? (snap.series as unknown[]).map(finite).filter((x): x is number => x !== null).slice(-14) : [],
    insights: Array.isArray(snap.insights) ? (snap.insights as { text?: unknown }[]).map((x) => str(x?.text, 400)).filter((x): x is string => !!x).slice(0, 4) : [],
    queries: Array.isArray(snap.queries)
      ? (snap.queries as Record<string, unknown>[]).filter((q) => q && typeof q.query === "string").slice(0, 6).map((q) => ({
          query: String(q.query).slice(0, 120),
          clicks: finite(q.clicks),
          impressions: finite(q.impressions),
          ctr: finite(q.ctr),
          position: finite(q.position),
          tag: str(q.tag, 12) ?? "top",
          dPosition: finite(q.dPosition),
        }))
      : [],
  };
}

/**
 * The quick wins the user last asked ContextBolt SEO for, as it saved them: the newest saved result of
 * search_console_quick_wins (list_research) and its answer (get_research). Two free reads that save nothing; the
 * board never runs the tool itself, which would save a new entry in the user's research each time.
 */
async function savedWins(call: Call): Promise<QuickWins> {
  const latest = researchItems(await call("list_research", { tool: "search_console_quick_wins", limit: 1 }))[0];
  if (!latest?.id) return { read: "seo-wins", empty: "none" };
  return { ...quickWins(await savedAnswer(call, latest.id, "its saved quick wins")), at: latest.at };
}

/** search_console_quick_wins' answer (src/lib/gsc.ts:378-407), and the words it says in place of rows (src/lib/gsc.ts:225-292). */
export function quickWins(raw: string): QuickWins {
  const t = unreceipt(raw);
  if (/^Your Google Search Console (isn't connected yet|connection is no longer valid)/.test(t)) return { read: "seo-wins", empty: "gsc-off" };
  if (/^This Google account has more than one Search Console property/.test(t)) return { read: "seo-wins", empty: "gsc-pick" };
  if (/^No verified Search Console properties/.test(t)) return { read: "seo-wins", empty: "gsc-none" };
  if (/^Search Console request failed: /.test(t)) throw new UnreadableError(`Search Console did not answer ContextBolt SEO: ${t.replace(/^Search Console request failed: /, "").slice(0, 160)}`);
  const none = /^No striking-distance queries found for (.+) \((\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})\) with at least ([\d,]+) impressions\./.exec(t);
  if (none) return { read: "seo-wins", site: none[1], from: none[2], to: none[3], min: num(none[4]), rows: [] };
  const head = /^SEO quick wins for (.+) \((\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})\):/.exec(t);
  if (!head) return unreadable("ContextBolt SEO", "its quick wins");
  const rows: NonNullable<QuickWins["rows"]> = [];
  for (const line of t.split("\n")) {
    const m = /^\d+\. (.+) - position ([\d.]+|n\/a), ([\d,]+|n\/a) impressions, ([\d,]+|n\/a) clicks, ([\d.]+%|n\/a) CTR$/.exec(line.trim());
    if (!m) continue;
    const ctr = num(m[5]);
    rows.push({ query: m[1].slice(0, 120), position: num(m[2]), impressions: num(m[3]), clicks: num(m[4]), ctr: ctr === null ? null : ctr / 100 });
  }
  if (!rows.length) unreadable("ContextBolt SEO", "its quick wins");
  return { read: "seo-wins", site: head[1], from: head[2], to: head[3], min: null, rows };
}

/** The newest saved AI visibility report (src/lib/ai-report.ts:749-771), with its rivals from the finding it keeps; else a single check. */
async function aiVisibility(call: Call): Promise<AiVisibility> {
  const reports = researchItems(await call("list_research", { tool: "ai_visibility_report", limit: 1 }));
  const r = reports[0];
  if (!r) {
    const checks = researchItems(await call("list_research", { tool: "ai_visibility", limit: 1 }));
    const c = checks[0];
    if (!c) return { read: "seo-ai", empty: "none" };
    const s = c.summary;
    return { read: "seo-ai", kind: "check", domain: str(s.domain), topic: str(s.topic), location: str(s.location), at: c.at, mentions: finite(s.mentions) };
  }
  let s = r.summary;
  // the saved list keeps only plain values (src/lib/research-store.ts:32-40); the finding keeps the rivals and the engines
  // (src/lib/findings.ts:824-836, its summary as JSON after "Last summary:")
  if (r.args) {
    const detail = text(await call("get_finding", { tool: "ai_visibility_report", args: r.args }));
    const at = detail.indexOf("\nLast summary:\n");
    if (at >= 0) {
      try {
        const full = JSON.parse(detail.slice(at + "\nLast summary:\n".length));
        if (full && typeof full === "object") s = { ...s, ...full };
      } catch {
        /* the plain values from the list still stand */
      }
    }
  }
  const eng = s.by_engine && typeof s.by_engine === "object" ? (s.by_engine as Record<string, unknown>) : null;
  return {
    read: "seo-ai",
    kind: "report",
    brand: str(s.brand, 80),
    domain: str(s.domain, 120),
    topic: str(s.topic, 120),
    location: str(s.location, 60),
    at: finite(s.report_at) ?? r.at,
    mentions: finite(s.mentions),
    answers: finite(s.topic_answers),
    share: finite(s.share_pct),
    rank: finite(s.rank),
    rankOf: finite(s.rank_of),
    leader: str(s.leader, 80),
    cited: finite(s.cited),
    pagesCited: finite(s.pages_cited),
    firstMonth: str(s.first_month, 20),
    engines: eng ? { google: finite(eng.google), chatgpt: finite(eng.chatgpt) } : null,
    sov: Array.isArray(s.sov)
      ? (s.sov as Record<string, unknown>[]).filter((x) => x && typeof x.brand === "string").slice(0, 6).map((x) => ({ brand: String(x.brand).slice(0, 60), mentions: finite(x.mentions), share: finite(x.share_pct), you: x.is_you === true }))
      : [],
    pages: Array.isArray(s.top_pages) ? (s.top_pages as Record<string, unknown>[]).filter((x) => x && typeof x.path === "string").slice(0, 4).map((x) => ({ path: String(x.path).slice(0, 120), cites: finite(x.cites) })) : [],
  };
}

/** Saved keyword checks (keyword_difficulty: src/lib/dataforseo.ts, its summary of volume, difficulty and the year's trend). */
export function keywords(v: unknown): Keywords {
  const rows = researchItems(v)
    .filter((x) => x.tool === "keyword_difficulty" && typeof x.summary.keyword === "string")
    .map((x) => ({
      keyword: String(x.summary.keyword).slice(0, 100),
      location: str(x.summary.location, 60),
      volume: finite(x.summary.search_volume),
      difficulty: finite(x.summary.difficulty),
      trend: finite(x.summary.trend_yoy),
      cpc: finite(x.summary.cpc),
      intent: str(x.summary.intent, 30),
      at: x.at,
      pinned: x.pinned,
    }));
  return { read: "seo-keywords", rows };
}

const KIND: Record<string, string> = {
  keyword_research: "Keyword ideas",
  keyword_difficulty: "Keyword",
  serp_overview: "Who ranks",
  domain_overview: "Domain",
  ranked_keywords: "Ranked keywords",
  competitor_keywords: "Competitors",
  backlink_overview: "Backlinks",
  referring_domains: "Referring domains",
  backlink_gap: "Backlink gap",
  ai_visibility: "AI visibility",
  ai_share_of_voice: "AI share of voice",
  ai_keyword_volume: "AI keyword volume",
  ai_answer: "AI answer",
  ai_visibility_report: "AI report",
  site_audit: "Site audit",
  page_audit: "Page audit",
  search_console_performance: "Search Console",
  search_console_quick_wins: "Quick wins",
  search_console_snapshot: "Search snapshot",
  search_console_query: "Search Console",
  inspect_url: "Index check",
  imported_research: "Imported",
};

const n0 = (v: unknown) => (finite(v) === null ? null : Math.round(v as number).toLocaleString("en-US"));

/** "SEO's share of a percent", the way SEO prints it (src/lib/ai-report.ts:794-799, fmtShare). */
export function shareText(pct: number): string {
  if (!(pct > 0)) return "0%";
  if (pct < 0.1) return "<0.1%";
  if (pct < 10) return `${Math.round(pct * 10) / 10}%`;
  return `${Math.round(pct)}%`;
}

/** One line of what a saved result found, from the values it keeps; null when there is nothing plain to say. */
export function researchLine(tool: string, s: Record<string, unknown>): string | null {
  const bits: (string | null)[] = [];
  const add = (v: string | null, word: string) => bits.push(v === null ? null : `${v} ${word}`);
  switch (tool) {
    case "keyword_difficulty":
      add(n0(s.search_volume), "a month");
      if (finite(s.difficulty) !== null) bits.push(`difficulty ${Math.round(s.difficulty as number)}`);
      break;
    case "keyword_research":
      add(n0(s.idea_count), "ideas");
      add(n0(s.low_difficulty_count), "easy");
      break;
    case "serp_overview":
      if (str(s.featured_snippet_owner)) bits.push(`snippet: ${str(s.featured_snippet_owner, 60)}`);
      add(n0(s.total_results), "results");
      break;
    case "domain_overview":
      add(n0(s.estimated_traffic), "visits a month");
      add(n0(s.ranked_keywords), "keywords");
      break;
    case "ranked_keywords":
    case "competitor_keywords":
      add(n0(s.shown), tool === "ranked_keywords" ? "keywords" : "competitors");
      break;
    case "ai_visibility":
      add(n0(s.mentions), "mentions");
      break;
    case "ai_visibility_report":
      if (finite(s.share_pct) !== null) bits.push(`${shareText(s.share_pct as number)} share of voice`);
      if (finite(s.rank) !== null && finite(s.rank_of) !== null) bits.push(`#${s.rank} of ${s.rank_of}`);
      break;
    case "site_audit":
    case "page_audit":
      add(n0(s.issues), "issues");
      add(n0(s.pages_checked), "pages");
      break;
    case "search_console_snapshot":
      add(n0(s.clicks), "clicks");
      add(n0(s.impressions), "impressions");
      break;
  }
  const out = bits.filter((b): b is string => !!b);
  return out.length ? out.join(" · ") : null;
}

export function research(v: unknown): Research {
  return {
    read: "seo-research",
    rows: researchItems(v).map((x) => ({ tool: x.tool, kind: KIND[x.tool] ?? x.tool.replace(/_/g, " "), label: x.label, line: researchLine(x.tool, x.summary), at: x.at, pinned: x.pinned })),
  };
}

/** check_credits (src/routes/mcp.ts:1379-1386): the total, this month's allowance with when it resets, and top-ups. */
export function credits(raw: string): Credits {
  const t = raw.trim();
  const total = /^You have ([\d,]+) ContextBolt SEO credits? available:/.exec(t);
  const month = /^- ([\d,]+) from this month's allowance, which resets on (\w+) (\d{1,2}), (\d{4})$/m.exec(t);
  const topup = /^- ([\d,]+) in top-up credits, which never expire$/m.exec(t);
  if (!total || !month || !topup) return unreadable("ContextBolt SEO", "your credits");
  const mi = MONTHS.indexOf(month![2]);
  const resets = mi >= 0 ? `${month![4]}-${String(mi + 1).padStart(2, "0")}-${month![3].padStart(2, "0")}` : null;
  return { read: "seo-credits", total: num(total![1])!, allowance: num(month![1])!, resets, topup: num(topup![1])! };
}

const hostOf = (url: string): string | null => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

/** export_bookmarks (src/routes/mcp.ts:576-653): a line with the total, then the newest as JSON. */
export function library(raw: string, now = Date.now()): Omit<Library, "topics"> {
  const t = raw.trim();
  if (/^No bookmarks to export yet\./.test(t)) return { read: "bm-library", total: 0, items: [] };
  const all = /^Exported all ([\d,]+) bookmarks as JSON\./.exec(t);
  const some = /^Exported bookmarks [\d,]+-[\d,]+ of ([\d,]+)/.exec(t);
  const json = /```json\n([\s\S]*)\n```\s*$/.exec(t);
  if (!(all || some) || !json) return unreadable("ContextBolt Bookmarks", "your library");
  let list: unknown;
  try {
    list = JSON.parse(json![1]);
  } catch {
    return unreadable("ContextBolt Bookmarks", "your library");
  }
  if (!Array.isArray(list)) return unreadable("ContextBolt Bookmarks", "your library");
  const items: Saved[] = (list as Record<string, unknown>[])
    .filter((x) => x && typeof x === "object" && typeof x.id === "string")
    .slice(0, 100)
    .map((x) => {
      const url = typeof x.url === "string" ? x.url.slice(0, 500) : "";
      const at = typeof x.saved_at === "string" ? Date.parse(x.saved_at) : NaN;
      const platform = str(x.platform, 20) ?? "web";
      return {
        id: String(x.id).slice(0, 120),
        platform,
        title: (str(x.title, 160) ?? str(x.content, 160) ?? "").replace(/\s+/g, " "),
        author: str(x.author, 60) ?? "",
        url: /^https?:\/\//i.test(url) ? url : "",
        host: platform === "web" ? hostOf(url) : null,
        tags: Array.isArray(x.tags) ? (x.tags as unknown[]).map((g) => str(g, 30)).filter((g): g is string => !!g).slice(0, 4) : [],
        topic: str(x.cluster_id, 60),
        at: Number.isFinite(at) && at <= now + 86_400_000 ? at : null,
      };
    });
  return { read: "bm-library", total: num((all ?? some)![1])!, items };
}

/** list_clusters (src/routes/mcp.ts:455-489): a line per topic, its name, id, how many and what it is about. */
export function clusters(raw: string): Topic[] {
  const t = raw.trim();
  if (/^No topic clusters found yet\./.test(t)) return [];
  if (!/^Topic clusters in your collection:/.test(t)) return unreadable("ContextBolt Bookmarks", "your topics");
  const out: Topic[] = [];
  for (const line of t.split("\n")) {
    const named = /^- (.+) \(([^()\s]+)\): ([\d,]+) bookmarks(?: — (.*))?$/.exec(line);
    const bare = named ? null : /^- ([^\s:]+): ([\d,]+) bookmarks$/.exec(line);
    if (named) out.push({ id: named[2].slice(0, 60), label: named[1].slice(0, 80), count: num(named[3]), description: named[4] ? named[4].slice(0, 200) : null });
    else if (bare) out.push({ id: bare[1].slice(0, 60), label: bare[1].slice(0, 80), count: num(bare[2]), description: null });
  }
  return out.slice(0, 100);
}

/** Bookmarks as search and a topic print them (src/routes/mcp.ts:259-290, formatBookmark), between "---" lines. */
export function blocks(raw: string, head: RegExp, none: string | null): Found[] {
  const t = raw.trim();
  if (none && t.startsWith(none)) return [];
  if (!head.test(t)) return unreadable("ContextBolt Bookmarks", "what it found");
  const body = t.replace(head, "").trim();
  const out: Found[] = [];
  for (const block of body.split(/\n\n---\n\n/)) {
    const lines = block.split("\n");
    const top = /^\[\d+\] (.*) \(([^()]*)\)$/.exec(lines[0] ?? "");
    const title = lines.findIndex((l) => l.startsWith("Title: "));
    let urlAt = -1;
    for (let i = lines.length - 1; i > 0; i--) if (lines[i].startsWith("URL: ")) { urlAt = i; break; }
    if (!top || title < 0 || urlAt < 0) continue;
    const content = lines.slice(title + 1, urlAt).join(" ").replace(/^Content: /, "").replace(/\s+/g, " ").trim();
    const after = lines.slice(urlAt + 1);
    const field = (name: string) => after.find((l) => l.startsWith(name + ": "))?.slice(name.length + 2).trim() ?? null;
    const url = lines[urlAt].slice(5).trim();
    out.push({
      author: top[1].trim().slice(0, 60),
      source: top[2].slice(0, 40),
      title: (lines[title].slice(7).trim() || content).slice(0, 160),
      url: /^https?:\/\//i.test(url) ? url.slice(0, 500) : "",
      tags: (field("Tags") ?? "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, 4),
      saved: field("Saved"),
      excerpt: content.slice(0, 220),
    });
  }
  return out;
}
